import "./App.css";
import Navbar from "./components/navigation/Navbar";
import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { useWindowSize } from "react-use";
import Loginpage from "./pages/LoginPage";
import BookingsPage from "./pages/BookingsPage";
import PrivateRoute from "./components/routing/PrivateRoute";
import RouterRedirect from "./components/routing/RouterRedirect";
import GymBookingPage from "./pages/GymBookingPage";
import ExplorePage from "./pages/ExplorePage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import MessageBox from "./components/utils/MessageBox";
import { Typography } from "@mui/material";
import ManageSubscriptionPage from "./pages/ProfilePage/ManageSubscriptionPage";
import PaymentsHistoryPage from "./pages/ProfilePage/PaymentsHistoryPage";
import AddPaymentCardPage from "./pages/ProfilePage/AddPaymentCardPage";
import BookingsHistoryPage from "./pages/ProfilePage/BookingsHistoryPage";
import AccountStatusBanner from "./components/utils/AccountStatusBanner";
import api from "./components/api";

function App() {
  const [mobile, setMobile] = useState(0);
  const [loggedIn, setLoggedIn] = useState(2);
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState({ lat: null, lon: null });
  const [accountSuspendedReason, setAccountSuspendedReason] = useState(null);
  const [locationChanged, setLocationChanged] = useState(false);

  const [showLogOutMsgBox, setShowLogOutMsgBox] = useState(false);

  const { height } = useWindowSize();
  const handleLogout = () => {
    localStorage.removeItem("fullpass_user");
    setLoggedIn(3);
    setUser(null);
    setShowLogOutMsgBox(false);
  };

  const theme = createTheme({
    typography: {
      fontFamily: "Urbanist",
    },
  });

  const calculateNavBarHeight = () => {
    if (mobile) return 0;
    if (window.innerWidth < 700) return 56;
    return 64;
  };

  const calculateBottomNavBarHeight = () => {
    return mobile ? 70 : 64;
  };

  const handleResize = () => {
    if (isMobile && window.screen.orientation.type.includes("portrait")) {
      setMobile(1);
    } else if (window.innerWidth < 700) {
      setMobile(1);
    } else if (isMobile) {
      setMobile(2);
    } else {
      setMobile(0);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    setLoggedIn(3);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getSelfUser = () => {
    api.getSelfUser((response) => {
      const prevUser = JSON.parse(localStorage.getItem("fullpass_user"));
      localStorage.setItem("fullpass_user", JSON.stringify({ ...prevUser, ...response }));
      setUser(response);
      setLoggedIn(1);
    }, handleLogout);
  };

  const handleSubscriptionStatus = () => {
    switch (user.subscriptionStatus) {
      case "EXPIRED":
        setAccountSuspendedReason("Tu membresía ha expirado. Renueva tu tarjeta o contactate con nosotros.");
        break;
      case "CANCELLED":
        setAccountSuspendedReason("Membresía cancelada. Haz click aquí para reactivarla.");
        break;
      default:
        setAccountSuspendedReason(null);
        break;
    }
  };

  useEffect(() => {
    if (user) {
      handleSubscriptionStatus();
    }
  }, [user]);

  useEffect(() => {
    if (!user && localStorage.getItem("fullpass_user")) {
      setUser(JSON.parse(localStorage.getItem("fullpass_user")));
      if (JSON.parse(localStorage.getItem("fullpass_user")).subscriptionStatus != "INACTIVE") {
        setUser(JSON.parse(localStorage.getItem("fullpass_user")));
        setLoggedIn(1);
        getSelfUser();
      }
    }
  }, []);

  return (
    <Router>
      <ThemeProvider theme={theme}>
        {loggedIn == 1 && (
          <Navbar
            user={user}
            mobile={mobile}
            handleLogout={() => setShowLogOutMsgBox(true)}
            locationChanged={locationChanged}
          />
        )}
        {loggedIn == 1 && accountSuspendedReason && (
          <AccountStatusBanner text={accountSuspendedReason} setLocationChanged={setLocationChanged} mobile={mobile} />
        )}
        {showLogOutMsgBox && (
          <MessageBox
            acceptText="Cerrar sesión"
            cancelText="Cancelar"
            onAccept={() => handleLogout()}
            onCancel={() => setShowLogOutMsgBox(false)}
          >
            <Typography
              style={{
                fontWeight: "600",
                fontSize: "36px",
                color: "white",
                textAlign: "center",
                paddingTop: "30px",
              }}
            >
              ¿Estás seguro de cerrar sesión?
            </Typography>
          </MessageBox>
        )}
        <Routes>
          <Route
            path="/"
            element={
              loggedIn == 1 ? (
                <Navigate to="/reservar" />
              ) : (
                <Loginpage mobile={mobile} setLoggedIn={setLoggedIn} setUser={setUser} />
              )
            }
          />
          <Route
            path="/reservar"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <BookingsPage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  location={location}
                  setLocation={setLocation}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/gimnasio/:gymId"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <GymBookingPage mobile={mobile} user={user} height={height - calculateNavBarHeight()} />
              </PrivateRoute>
            }
          />
          <Route
            path="/explorar"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <ExplorePage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  location={location}
                  setLocation={setLocation}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <ProfilePage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  setLoggedIn={setLoggedIn}
                  setUser={setUser}
                  handleLogout={() => setShowLogOutMsgBox(true)}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/membresia"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <ManageSubscriptionPage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  setLoggedIn={setLoggedIn}
                  setUser={setUser}
                  handleLogout={() => setShowLogOutMsgBox(true)}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/pagos/historial"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <PaymentsHistoryPage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  setLoggedIn={setLoggedIn}
                  setUser={setUser}
                  handleLogout={() => setShowLogOutMsgBox(true)}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/pagos/tarjeta"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <AddPaymentCardPage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  setLoggedIn={setLoggedIn}
                  setUser={setUser}
                  handleLogout={() => setShowLogOutMsgBox(true)}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/reservas/historial"
            element={
              <PrivateRoute loggedIn={loggedIn}>
                <BookingsHistoryPage
                  mobile={mobile}
                  user={user}
                  height={height - calculateNavBarHeight()}
                  setLoggedIn={setLoggedIn}
                  setUser={setUser}
                  handleLogout={() => setShowLogOutMsgBox(true)}
                />
              </PrivateRoute>
            }
          />
          <Route path="/*" element={<Navigate to="/" />} />
        </Routes>
      </ThemeProvider>
    </Router>
  );
}

export default App;
